import { Button } from "@/components/ui/button";
import { AppStoreBadge, GooglePlayBadge } from "@/components/AppStoreBadges";
import { useTerritory } from "@/contexts/TerritoryContext";
import { Illustration } from "@/components/Illustration";
import { Calculator, Gift, Star, Zap, Check } from "lucide-react";

/** Flat purple hero in the platform's own visual language: Owlbert and three product cards. */
export default function Hero() {
  const { territory } = useTerritory();

  return (
    <section className="relative overflow-hidden bg-primary text-primary-foreground py-16 md:py-24 lg:py-28">
      <div className="container relative px-4 md:px-6 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-12 lg:gap-16 items-center">
          <div className="space-y-7 md:space-y-8 text-center lg:text-left">
            <div className="space-y-5">
              <span className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full bg-white/15">
                <Star className="w-3.5 h-3.5 text-amber-300" />Number one edtech company in the Caribbean, TIME magazine
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight" data-testid="text-hero-headline">
                {territory.hero.headline}{" "}
                <span className="text-amber-300">{territory.hero.highlightedText}</span>
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-primary-foreground/85 max-w-xl leading-relaxed mx-auto lg:mx-0" data-testid="text-hero-description">
                {territory.hero.description}
              </p>
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-2">
                {["No setup needed", "Works on any device", "Cancel anytime"].map((t) => (
                  <span key={t} className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-full bg-white/15">
                    <span className="w-4 h-4 rounded-full bg-amber-400 text-amber-950 flex items-center justify-center"><Check className="w-3 h-3" strokeWidth={3} /></span>{t}
                  </span>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3">
              <Button size="lg" className="h-14 px-8 text-base rounded-xl bg-amber-400 text-amber-950 hover:bg-amber-300 font-bold" data-testid="button-get-started" onClick={() => window.location.href = '/signup'}>
                Get started
              </Button>
              <Button size="lg" variant="secondary" className="h-14 px-8 text-base rounded-xl bg-white/15 hover:bg-white/25 text-primary-foreground font-semibold" data-testid="button-talk-to-us" onClick={() => window.location.href = '/contact'}>
                Text us
              </Button>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3">
              <p className="text-primary-foreground/60 text-xs font-medium">Also on</p>
              <AppStoreBadge tone="dark" />
              <GooglePlayBadge tone="dark" />
            </div>
          </div>

          {/* Product composition: Owlbert plus three flat cards straight out of the app */}
          <div className="relative mx-auto w-full max-w-md lg:max-w-none h-[420px] sm:h-[460px]" data-testid="hero-image-container" aria-hidden>
            <div className="absolute left-1/2 -translate-x-1/2 bottom-0 lg:left-auto lg:translate-x-0 lg:right-6">
              <Illustration name="owl-rocket" size={260} className="drop-shadow-[0_12px_0_rgba(0,0,0,0.15)]" />
            </div>
            <div className="absolute top-2 left-0 sm:left-2 w-[240px] rounded-2xl bg-white text-foreground p-4 shadow-lg -rotate-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-500 text-white flex items-center justify-center -rotate-2 shadow-sm"><Calculator className="w-5 h-5" /></div>
                <div className="min-w-0"><div className="font-semibold leading-tight">Mathematics</div><div className="text-xs text-muted-foreground">Level 5 · 3,840 pts</div></div>
              </div>
              <div className="mt-3 h-2 rounded-full bg-blue-500/15 overflow-hidden"><div className="h-full w-[68%] rounded-full bg-blue-500" /></div>
              <div className="mt-1.5 text-[11px] text-blue-900/70">660 XP to level 6</div>
            </div>
            <div className="absolute top-24 sm:top-28 right-0 sm:right-2 w-[210px] rounded-2xl bg-white text-foreground p-4 shadow-lg rotate-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-500 text-white flex items-center justify-center rotate-2 shadow-sm"><Zap className="w-5 h-5" /></div>
                <div><div className="text-2xl font-bold tabular-nums leading-none">9/10</div><div className="text-xs text-muted-foreground mt-1">Great job. +500 XP</div></div>
              </div>
            </div>
            <div className="absolute top-52 sm:top-60 left-2 sm:left-8 w-[250px] rounded-2xl bg-amber-50 text-foreground border border-amber-300 p-4 shadow-lg rotate-1">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500 text-white flex items-center justify-center -rotate-3 shadow-sm"><Gift className="w-5 h-5" /></div>
                <div className="min-w-0"><div className="font-semibold leading-tight">You won a gift card</div><div className="text-xs text-muted-foreground">Claim it and it is texted to a parent</div></div>
              </div>
            </div>
          </div>
        </div>

        {/* Trust bar */}
        <div className="mt-14 md:mt-16 pt-8 border-t border-white/15 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-10 text-sm text-primary-foreground/85">
          <div className="flex items-center gap-3">
            <div className="flex -space-x-2">
              {[["J", "bg-amber-400 text-amber-950"], ["K", "bg-emerald-400 text-emerald-950"], ["A", "bg-sky-300 text-sky-950"], ["M", "bg-rose-300 text-rose-950"]].map(([l, c]) => (
                <div key={l} className={`w-8 h-8 rounded-full border-2 border-primary flex items-center justify-center text-xs font-bold ${c}`}>{l}</div>
              ))}
            </div>
            <span className="font-semibold">{territory.hero.trustLine}</span>
          </div>
          <div className="hidden sm:block w-px h-6 bg-white/20" />
          <span className="font-medium">Endorsed by the Ministry of Education. Teaching online since 2011.</span>
        </div>
      </div>
    </section>
  );
}
